/**
 * InspectionFlowSimple — mobile-first inspection flow.
 *
 * Screens: START → (optional NOTIFY for Periodic) → ROOM (one at a time) → REVIEW.
 * Perf trick: photos render via `URL.createObjectURL` in <100ms, upload runs in the background.
 * Extras: custom room labels (tap the pencil), Blinds-need-replacing size capture,
 * Camera + Library photo sources, modernised buttons with press feedback.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Camera, CheckCircle, Warning, ArrowLeft, ArrowRight, ArrowsClockwise,
  DotsThreeVertical, ClipboardText, FilePdf, FloppyDisk, Play,
  ArrowClockwise, PaperPlaneTilt, Image as ImageIcon, PencilSimple,
  Rows as Blinds,
} from "@phosphor-icons/react";
import VoiceDictateButton from "@/components/VoiceDictateButton";
import InspectRadarGrid from "@/components/InspectRadarGrid";

// ─────────── Field Radar rating palette (navy + teal + traffic-light) ───────────
const RATINGS = [
  { key: "good",    label: "Good",   short: "GOOD",   solid: "bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800", dot: "bg-emerald-500", ring: "ring-emerald-500" },
  { key: "fair",    label: "Fair",   short: "FAIR",   solid: "bg-amber-500 hover:bg-amber-600 active:bg-amber-700",         dot: "bg-amber-500",   ring: "ring-amber-500" },
  { key: "damaged", label: "Damage", short: "DAMAGE", solid: "bg-red-600 hover:bg-red-700 active:bg-red-800",                 dot: "bg-red-500",     ring: "ring-red-500" },
  { key: "needs",   label: "Attn",   short: "ATTN",   solid: "bg-fuchsia-600 hover:bg-fuchsia-700 active:bg-fuchsia-800",     dot: "bg-fuchsia-500", ring: "ring-fuchsia-500" },
];

const SERVER_MAP      = { good: "good", fair: "fair", damaged: "broken", needs: "poor" };
const SERVER_MAP_BACK = { great: "good", good: "good", fair: "fair", poor: "needs", broken: "damaged", na: "" };

const BLIND_HEIGHTS = ["64", "72"];
const BLIND_WIDTHS  = ["23", "27", "29", "32", "34", "36", "48"];

// Convenience: the display label for a room (custom label or canonical key)
const roomLabel = (r) => (r?.label && String(r.label).trim()) || r?.room || "";

export default function InspectionFlowSimple({ jobId, phase, onClose, onJobReload, onSwitchClassic }) {
  const [state, setState] = useState(null);
  const [job, setJob] = useState(null);
  const [screen, setScreen] = useState("start");
  const [roomIdx, setRoomIdx] = useState(0);
  const [notesDraft, setNotesDraft] = useState("");
  const [labelDraft, setLabelDraft] = useState("");
  const [editingLabel, setEditingLabel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [optimistic, setOptimistic] = useState({});
  const [blindsDraft, setBlindsDraft] = useState({ enabled: false, entries: [{ width: "", height: "", count: 1, note: "" }] });
  const [applianceOpen, setApplianceOpen] = useState(false);
  const cameraRef  = useRef(null);
  const libraryRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [insp, jobResp] = await Promise.all([
        api.get(`/jobs/${jobId}/inspection/${phase}`),
        api.get(`/jobs/${jobId}`),
      ]);
      setState(insp.data);
      setJob(jobResp.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load inspection");
    }
  }, [jobId, phase]);
  useEffect(() => { load(); }, [load]);

  const rooms = state?.rooms || [];
  const total = rooms.length;
  const completed = useMemo(() => rooms.filter(r => r.completed).length, [rooms]);
  const currentRoom = rooms[roomIdx];

  // Sync editors when the room changes
  useEffect(() => {
    setNotesDraft(currentRoom?.notes || "");
    setLabelDraft(currentRoom?.label || "");
    setEditingLabel(false);
    const b = currentRoom?.metadata?.blinds;
    // Backward-compat: legacy single object → wrap into a 1-entry array
    let entries = [];
    let enabled = false;
    if (Array.isArray(b)) {
      entries = b.map(e => ({
        width: e?.width || "", height: e?.height || "",
        count: e?.count || 1, note: e?.note || "",
      }));
      enabled = entries.length > 0;
    } else if (b && typeof b === "object") {
      enabled = !!b.needs_replacement;
      entries = [{ width: b.width || "", height: b.height || "", count: b.count || 1, note: b.note || "" }];
    }
    if (entries.length === 0) entries = [{ width: "", height: "", count: 1, note: "" }];
    setBlindsDraft({ enabled, entries });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomIdx, currentRoom?.room]);

  const patchRoom = async (roomName, body) => {
    setSaving(true);
    try {
      await api.patch(`/jobs/${jobId}/inspection/${phase}/room?room=${encodeURIComponent(roomName)}`, body);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const setRating = (ratingKey) => {
    if (!currentRoom) return;
    patchRoom(currentRoom.room, {
      condition: SERVER_MAP[ratingKey] || "good",
      completed: true,
      notes: notesDraft,
    });
  };

  const saveLabel = async () => {
    if (!currentRoom) return;
    const clean = labelDraft.trim();
    if (clean === (currentRoom.label || "")) { setEditingLabel(false); return; }
    await patchRoom(currentRoom.room, { label: clean });
    setEditingLabel(false);
    toast.success(clean ? `Renamed to "${clean}"` : "Custom label removed");
  };

  const saveBlinds = async (nextDraft) => {
    if (!currentRoom) return;
    setBlindsDraft(nextDraft);
    // Empty list when disabled — server will store [] so summary knows to skip.
    const payload = nextDraft.enabled
      ? nextDraft.entries.map(e => ({
          width: e.width, height: e.height,
          count: Number(e.count) || 1, note: e.note,
        }))
      : [];
    await patchRoom(currentRoom.room, {
      metadata: { ...(currentRoom.metadata || {}), blinds: payload },
    });
  };

  const updateBlindEntry = (idx, patch) => {
    const nextEntries = blindsDraft.entries.map((e, i) => i === idx ? { ...e, ...patch } : e);
    saveBlinds({ ...blindsDraft, entries: nextEntries });
  };
  const addBlindEntry = () => {
    saveBlinds({ ...blindsDraft, entries: [...blindsDraft.entries, { width: "", height: "", count: 1, note: "" }] });
  };
  const removeBlindEntry = (idx) => {
    const next = blindsDraft.entries.filter((_, i) => i !== idx);
    saveBlinds({ ...blindsDraft, entries: next.length ? next : [{ width: "", height: "", count: 1, note: "" }] });
  };

  const goNext = () => {
    if (!currentRoom) return;
    if (notesDraft !== (currentRoom.notes || "")) {
      api.patch(`/jobs/${jobId}/inspection/${phase}/room?room=${encodeURIComponent(currentRoom.room)}`,
        { notes: notesDraft }).catch(() => {});
    }
    if (roomIdx + 1 >= total) setScreen("review");
    else setRoomIdx(roomIdx + 1);
  };
  const goPrev = () => { if (roomIdx > 0) setRoomIdx(roomIdx - 1); };

  // ── Photo pipeline with optimistic previews ──
  const uploadOne = async (opt) => {
    if (!currentRoom) return;
    const fd = new FormData();
    fd.append("file", opt.file);
    fd.append("room", currentRoom.room);
    try {
      await api.post(`/jobs/${jobId}/inspection/${phase}/room/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90_000,
      });
      setOptimistic(prev => ({ ...prev,
        [currentRoom.room]: (prev[currentRoom.room] || []).filter(o => o.optId !== opt.optId),
      }));
      try { URL.revokeObjectURL(opt.localUrl); } catch {}
    } catch {
      setOptimistic(prev => ({ ...prev,
        [currentRoom.room]: (prev[currentRoom.room] || []).map(o =>
          o.optId === opt.optId ? { ...o, uploading: false, error: true } : o),
      }));
    }
  };

  const onPhoto = async (files) => {
    if (!currentRoom || !files?.length) return;
    const arr = Array.from(files);
    const newOpts = arr.map(f => ({
      optId: `opt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      localUrl: URL.createObjectURL(f),
      file: f, uploading: true, error: false,
    }));
    setOptimistic(prev => ({ ...prev,
      [currentRoom.room]: [...(prev[currentRoom.room] || []), ...newOpts],
    }));
    for (const opt of newOpts) await uploadOne(opt);
    load();
  };

  const retryUpload = async (opt) => {
    if (!currentRoom) return;
    setOptimistic(prev => ({ ...prev,
      [currentRoom.room]: (prev[currentRoom.room] || []).map(o =>
        o.optId === opt.optId ? { ...o, uploading: true, error: false } : o),
    }));
    await uploadOne(opt);
    load();
  };

  const finalize = async () => {
    if (!window.confirm("Finalize this inspection? It'll save to Completed Reports and generate the PDF.")) return;
    const force = completed < total;
    if (force && !window.confirm(`Only ${completed}/${total} rooms are rated. Finalize anyway?`)) return;
    setFinalizing(true);
    try {
      const url = `/jobs/${jobId}/finalize-inspection${force ? "?force=true" : ""}`;
      const { data } = await api.post(url);
      toast.success(data.already_completed ? "Already finalized" : "Inspection saved to Reports");
      if (onJobReload) await onJobReload();
      setTimeout(() => { window.location.href = "/admin/completed-inspections"; }, 500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not finalize");
    } finally { setFinalizing(false); }
  };

  if (!state || !job) {
    return (
      <div data-testid="insp-simple-loading" className="fixed inset-0 z-[80] bg-[#0f172a] text-white flex items-center justify-center">
        <ArrowsClockwise className="animate-spin" size={32}/>
      </div>
    );
  }

  const phaseLabel = phase === "move_in" ? "Move-In" : phase === "move_out" ? "Move-Out" : "Inspection";
  const inspectionType = String(job?.inspection_type || "").toLowerCase();
  const isPeriodic = inspectionType === "periodic";

  const notifyTenant = async () => {
    setNotifyBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/inspection/notify-tenant`, { channels: ["sms", "email"] });
      setNotifyResult(r.data);
      if (r.data.already_sent) toast.info("Tenant was notified in the last 6 hours — skipping duplicate.");
      else if (r.data.sent_sms || r.data.sent_email) {
        const ch = [
          r.data.sent_sms  ? (r.data.sms_status === "mocked" ? "SMS (mocked)" : "SMS") : null,
          r.data.sent_email ? "email" : null,
        ].filter(Boolean).join(" + ");
        toast.success(`Tenant notified via ${ch}`);
      } else if (!r.data.phone_on_file && !r.data.email_on_file) {
        toast.error("No phone or email on file — starting walk without notification.");
      } else toast.error("Could not send — starting walk anyway.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Notify failed");
    } finally {
      setNotifyBusy(false);
      setScreen("room"); setRoomIdx(0);
    }
  };

  // ─────────────── GRID SCREEN (Room × Trade grid, Grok-style) ───────────────
  if (screen === "grid") {
    const photoField = phase === "move_out" ? "before_photos" : "after_photos";
    const photosByRoom = (job?.[photoField] || []).reduce((acc, p) => {
      if (!p.inspection_room) return acc;
      (acc[p.inspection_room] = acc[p.inspection_room] || []).push(p);
      return acc;
    }, {});
    const roomsEnriched = rooms.map(r => ({ ...r, _photos: photosByRoom[r.room] || [] }));
    return (
      <div data-testid="insp-simple-grid" className="fixed inset-0 z-[80] bg-white text-slate-900 flex flex-col">
        <TopBar
          title={`${phaseLabel} · Grid`}
          onClose={onClose}
          onSwitchClassic={onSwitchClassic}
          onBackToStart={() => setScreen("start")}
        />
        <div className="flex-1 overflow-y-auto">
          <InspectRadarGrid
            jobId={jobId}
            phase={phase}
            rooms={roomsEnriched}
            onRoomsChanged={async () => { await load(); if (onJobReload) await onJobReload(); }}
            isSaving={saving}
          />
        </div>
      </div>
    );
  }

  // ─────────────── START SCREEN ───────────────
  if (screen === "start") {
    return (
      <div data-testid="insp-simple-start" className="fixed inset-0 z-[80] bg-[#0f172a] text-white flex flex-col">
        <TopBar title={phaseLabel} onClose={onClose} onSwitchClassic={onSwitchClassic}/>
        <div className="flex-1 overflow-y-auto p-6 pb-24 flex flex-col justify-center items-center text-center">
          <div className="max-w-md w-full">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC] mb-2">Ready to inspect</div>
            <div className="font-display font-black text-3xl md:text-4xl leading-tight">{phaseLabel}</div>
            <div className="mt-2 text-lg font-bold text-white/90 truncate">
              {job.property_address}
              {job.unit && <span className="ml-2 px-2 py-0.5 bg-[#00C4CC] text-[#0f172a] rounded text-sm">#{job.unit}</span>}
            </div>
            <div className="mt-1 text-sm text-white/60">
              {total} room{total === 1 ? "" : "s"} to walk · {completed} already rated
            </div>
            <div className="mt-8 grid grid-cols-2 gap-2 text-left">
              {rooms.slice(0, 8).map(r => (
                <div key={r.room} className="p-2.5 bg-white/5 border border-white/10 rounded-lg flex items-center gap-2 text-sm">
                  {r.completed
                    ? <CheckCircle weight="fill" size={16} className="text-emerald-400 shrink-0"/>
                    : <div className="h-4 w-4 border-2 border-white/40 shrink-0 rounded"/>}
                  <span className="truncate">{roomLabel(r)}</span>
                </div>
              ))}
              {rooms.length > 8 && (
                <div className="p-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white/60">
                  +{rooms.length - 8} more
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 bg-[#0f172a] border-t border-white/10 sticky bottom-0 space-y-2">
          <button
            data-testid="insp-simple-start-btn"
            onClick={() => { if (isPeriodic) { setScreen("notify"); return; } setScreen("room"); setRoomIdx(0); }}
            className="w-full h-14 rounded-2xl bg-[#00C4CC] hover:bg-[#00d4dd] active:scale-[0.98] text-[#0f172a] font-black uppercase tracking-widest text-base inline-flex items-center justify-center gap-2 shadow-[0_8px_24px_-8px_rgba(0,196,204,0.6)] transition-transform"
          >
            <Play weight="fill" size={20}/> {completed > 0 ? "Continue Inspection" : "Start Inspection"}
          </button>
          <button
            data-testid="insp-simple-grid-btn"
            onClick={() => setScreen("grid")}
            className="w-full h-11 rounded-xl bg-fuchsia-600/80 hover:bg-fuchsia-600 active:scale-[0.98] text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 transition-transform"
          >
            Grid view · rooms × trades
          </button>
        </div>
      </div>
    );
  }

  // ─────────────── NOTIFY SCREEN (Periodic only) ───────────────
  if (screen === "notify") {
    const tenantFirst = ((job.customer_name || "the tenant").split(" ")[0] || "the tenant");
    return (
      <div data-testid="insp-simple-notify" className="fixed inset-0 z-[80] bg-[#0f172a] text-white flex flex-col">
        <TopBar title={phaseLabel} onClose={onClose} onSwitchClassic={onSwitchClassic}/>
        <div className="flex-1 overflow-y-auto p-6 pb-24 flex flex-col justify-center items-center">
          <div className="max-w-md w-full text-center">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC] mb-2">Heads up</div>
            <div className="font-display font-black text-3xl leading-tight">Notify {tenantFirst}?</div>
            <div className="mt-2 text-sm text-white/70">
              Send a quick SMS + email letting them know you're stopping by.
            </div>
            <div className="mt-4 flex items-center gap-2 justify-center flex-wrap text-[11px] font-black uppercase tracking-widest">
              {notifyResult ? (
                <>
                  {notifyResult.phone_on_file
                    ? <span className={`inline-flex items-center gap-1 h-6 px-2 rounded-full border ${notifyResult.sent_sms ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-amber-500/20 text-amber-300 border-amber-500/40"}`}>SMS {notifyResult.sent_sms ? "sent" : notifyResult.sms_status || "skipped"}</span>
                    : <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-white/5 text-white/40 border border-white/10">No phone</span>}
                  {notifyResult.email_on_file
                    ? <span className={`inline-flex items-center gap-1 h-6 px-2 rounded-full border ${notifyResult.sent_email ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/40" : "bg-amber-500/20 text-amber-300 border-amber-500/40"}`}>Email {notifyResult.sent_email ? "sent" : "skipped"}</span>
                    : <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-white/5 text-white/40 border border-white/10">No email</span>}
                </>
              ) : (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-white/5 text-white/60 border border-white/10">
                  Will use phone + email on file
                </span>
              )}
            </div>
            <div className="mt-4 p-3 bg-white/5 border border-white/10 rounded-xl text-left text-sm text-white/80 italic">
              "Hi {tenantFirst}, we're stopping by for a quick periodic inspection today. Sorry for any inconvenience — reach out if this doesn't work."
            </div>
            <div className="mt-6 space-y-2">
              <button data-testid="insp-simple-notify-send" onClick={notifyTenant} disabled={notifyBusy}
                className="w-full h-14 rounded-2xl bg-[#00C4CC] hover:bg-[#00d4dd] active:scale-[0.98] text-[#0f172a] font-black uppercase tracking-widest text-base inline-flex items-center justify-center gap-2 disabled:opacity-40 shadow-[0_8px_24px_-8px_rgba(0,196,204,0.6)] transition-transform">
                {notifyBusy ? <ArrowsClockwise className="animate-spin" size={18}/> : <PaperPlaneTilt weight="fill" size={18}/>}
                Notify Tenant &amp; Start
              </button>
              <button data-testid="insp-simple-notify-skip" onClick={() => { setScreen("room"); setRoomIdx(0); }}
                className="w-full h-12 rounded-2xl bg-white/5 hover:bg-white/10 active:scale-[0.98] text-white/90 font-black uppercase tracking-widest text-xs border-2 border-white/20 inline-flex items-center justify-center gap-2 transition-transform">
                Skip — no notification needed
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────── REVIEW SCREEN ───────────────
  if (screen === "review") {
    const flagged = rooms.filter(r => ["fair", "poor", "broken"].includes(r.condition));
    // Blinds can now be a list of entries per room OR the legacy single object.
    // Roll them all up into a flat list of orders for the turn crew.
    const blindOrders = [];
    rooms.forEach(r => {
      const b = r?.metadata?.blinds;
      if (Array.isArray(b)) {
        b.forEach(e => {
          if (e && (e.width || e.height || e.count)) {
            blindOrders.push({ room: r, entry: e });
          }
        });
      } else if (b && typeof b === "object" && b.needs_replacement) {
        blindOrders.push({ room: r, entry: b });
      }
    });
    return (
      <div data-testid="insp-simple-review" className="fixed inset-0 z-[80] bg-[#f4f4f5] flex flex-col">
        <TopBar dark title={`${phaseLabel} · Review`} onClose={onClose} onSwitchClassic={onSwitchClassic}/>
        <div className="flex-1 overflow-y-auto p-4 pb-40 max-w-2xl mx-auto w-full">
          <div className="rugged-card p-4 bg-white rounded-2xl">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Summary</div>
            <div className="mt-1 font-display font-black text-2xl">{completed} of {total} rooms rated</div>
            <div className="mt-1 text-sm text-slate-500 truncate">
              {job.property_address}{job.unit ? ` · #${job.unit}` : ""}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-widest">
              {RATINGS.map(r => {
                const count = rooms.filter(x => SERVER_MAP_BACK[x.condition] === r.key).length;
                if (!count) return null;
                return (
                  <span key={r.key} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-white ${r.solid.split(" ")[0]}`}>
                    <span className={`h-2 w-2 rounded-full bg-white/70`}/>
                    {count} {r.label}
                  </span>
                );
              })}
            </div>
          </div>

          {blindOrders.length > 0 && (
            <div className="mt-4 rugged-card overflow-hidden rounded-2xl" data-testid="review-blinds-block">
              <div className="p-3 bg-fuchsia-50 border-b-2 border-fuchsia-500 flex items-center gap-2">
                <Blinds weight="fill" size={18} className="text-fuchsia-700"/>
                <div className="font-black uppercase text-sm tracking-widest text-fuchsia-900">
                  {blindOrders.length} blind order{blindOrders.length === 1 ? "" : "s"} for turn crew
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {blindOrders.map((o, i) => (
                  <div key={`${o.room.room}-${i}`} className="p-3" data-testid={`review-blinds-row-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-black text-sm truncate flex-1">{roomLabel(o.room)}</span>
                      <span className="inline-flex items-center h-6 px-2 bg-fuchsia-600 text-white text-[10px] font-black uppercase tracking-widest rounded-full font-mono">
                        {o.entry.width || "—"}" W × {o.entry.height || "—"}" H
                      </span>
                    </div>
                    {((o.entry.count || 0) > 1 || o.entry.note) && (
                      <div className="text-xs text-slate-600 mt-0.5">
                        {(o.entry.count || 0) > 1 && <b>Qty {o.entry.count}</b>}
                        {(o.entry.count || 0) > 1 && o.entry.note && " · "}
                        {o.entry.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {flagged.length > 0 && (
            <div className="mt-4 rugged-card overflow-hidden rounded-2xl">
              <div className="p-3 bg-red-50 border-b-2 border-red-500 flex items-center gap-2">
                <Warning weight="fill" size={18} className="text-red-600"/>
                <div className="font-black uppercase text-sm tracking-widest text-red-900">
                  {flagged.length} flagged item{flagged.length === 1 ? "" : "s"}
                </div>
              </div>
              <div className="divide-y-2 divide-slate-100">
                {flagged.map((r, i) => {
                  const rk = SERVER_MAP_BACK[r.condition] || "fair";
                  const meta = RATINGS.find(x => x.key === rk) || RATINGS[1];
                  return (
                    <button key={r.room} type="button"
                      onClick={() => { setRoomIdx(rooms.findIndex(x => x.room === r.room)); setScreen("room"); }}
                      data-testid={`insp-simple-review-flagged-${i}`}
                      className="w-full text-left p-3 flex items-start gap-3 hover:bg-slate-50 active:bg-slate-100">
                      <span className={`inline-flex items-center h-6 px-2 rounded-full text-[10px] font-black uppercase tracking-widest text-white ${meta.solid.split(" ")[0]}`}>
                        {meta.short}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-black text-base">{roomLabel(r)}</div>
                        {r.notes && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{r.notes}</div>}
                        <div className="text-[10px] text-slate-400 mt-0.5">{(r.photos || []).length} photo{(r.photos || []).length === 1 ? "" : "s"}</div>
                      </div>
                      <ArrowRight weight="bold" size={16} className="text-slate-400 mt-1"/>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-4 rugged-card overflow-hidden rounded-2xl">
            <div className="p-3 border-b-2 border-slate-200 font-black uppercase text-sm tracking-widest">All rooms</div>
            <div className="divide-y divide-slate-100">
              {rooms.map((r, i) => {
                const rk = SERVER_MAP_BACK[r.condition] || "";
                const meta = RATINGS.find(x => x.key === rk);
                return (
                  <button key={r.room} type="button"
                    onClick={() => { setRoomIdx(i); setScreen("room"); }}
                    data-testid={`insp-simple-review-room-${i}`}
                    className="w-full text-left p-3 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100">
                    <span className={`h-3 w-3 rounded-full ${meta ? meta.solid.split(" ")[0] : "bg-slate-300"}`}/>
                    <span className="flex-1 font-black text-sm truncate">{roomLabel(r)}</span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      {meta?.label || "not rated"}
                      {(r.photos || []).length > 0 && ` · ${(r.photos || []).length}📸`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#0f172a] p-3 pb-[calc(env(safe-area-inset-bottom,0)+12px)] space-y-2">
          <div className="max-w-2xl mx-auto space-y-2">
            <div className="flex gap-2">
              <button type="button" onClick={() => setScreen("room")}
                data-testid="insp-simple-back-to-rooms"
                className="h-11 px-4 rounded-xl border-2 border-slate-300 hover:border-[#0f172a] active:scale-[0.98] text-slate-700 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 transition-transform">
                <ArrowLeft weight="bold" size={13}/> Back to rooms
              </button>
              <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/jobs/${jobId}/blind-order-sheet`}
                target="_blank" rel="noreferrer"
                data-testid="insp-simple-blind-sheet"
                className="h-11 px-4 rounded-xl border-2 border-fuchsia-300 hover:border-fuchsia-600 hover:bg-fuchsia-50 text-fuchsia-800 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
                <FilePdf weight="bold" size={13}/> Blind Order
              </a>
              <a href={`/admin/completed-inspections`}
                data-testid="insp-simple-pdf"
                className="h-11 px-4 rounded-xl border-2 border-slate-300 hover:border-[#0f172a] text-slate-700 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
                <FilePdf weight="bold" size={13}/> Reports
              </a>
            </div>
            <button type="button" onClick={finalize} disabled={finalizing}
              data-testid="insp-simple-finalize"
              className="w-full h-14 rounded-2xl bg-[#00C4CC] hover:bg-[#00d4dd] active:scale-[0.98] text-[#0f172a] font-black uppercase tracking-widest text-base inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_8px_24px_-8px_rgba(0,196,204,0.6)] transition-transform">
              {finalizing ? <ArrowsClockwise className="animate-spin" size={18}/> : <CheckCircle weight="fill" size={18}/>}
              Finalize inspection
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────── ROOM SCREEN ───────────────
  const roomOpts = optimistic[currentRoom.room] || [];
  const serverPhotos = currentRoom.photos || [];
  const currentRating = SERVER_MAP_BACK[currentRoom.condition] || "";
  const showBlindsSection = ["damaged", "needs"].includes(currentRating) || blindsDraft.enabled;
  return (
    <div data-testid="insp-simple-room" className="fixed inset-0 z-[80] bg-[#f4f4f5] flex flex-col">
      <TopBar dark title={phaseLabel} onClose={onClose} onSwitchClassic={onSwitchClassic}
        subtitle={
          <div className="flex items-center gap-2 text-white/70 text-xs font-black uppercase tracking-widest">
            <span>Room {roomIdx + 1} of {total}</span>
            {saving && <ArrowsClockwise className="animate-spin" size={11}/>}
          </div>
        }/>
      <div className="h-1.5 bg-slate-800/40 relative">
        <div className="absolute top-0 left-0 h-full bg-[#00C4CC] transition-all duration-300"
          style={{ width: `${total ? ((roomIdx + (currentRoom.completed ? 1 : 0)) / total) * 100 : 0}%` }}
          data-testid="insp-simple-progress"/>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-[220px]">
        <div className="max-w-2xl mx-auto">
          {/* Room name + inline editor */}
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                Room
                {currentRoom.label && (
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 normal-case">canonical: {currentRoom.room}</span>
                )}
              </div>
              {editingLabel ? (
                <div className="mt-1 flex items-center gap-2">
                  <input autoFocus type="text" value={labelDraft}
                    onChange={e => setLabelDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") saveLabel(); if (e.key === "Escape") setEditingLabel(false); }}
                    placeholder={currentRoom.room}
                    maxLength={40}
                    data-testid="insp-simple-label-input"
                    className="flex-1 h-11 px-3 rounded-xl border-2 border-[#00C4CC] focus:border-[#00C4CC] font-display font-black text-2xl bg-white outline-none"/>
                  <button type="button" onClick={saveLabel} data-testid="insp-simple-label-save"
                    className="h-11 px-3 rounded-xl bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest active:scale-95 transition-transform">Save</button>
                </div>
              ) : (
                <button type="button" onClick={() => setEditingLabel(true)}
                  data-testid="insp-simple-label-edit"
                  className="mt-1 group flex items-center gap-2 text-left">
                  <div className="font-display font-black text-3xl md:text-4xl leading-tight">
                    {roomLabel(currentRoom)}
                  </div>
                  <PencilSimple weight="bold" size={16} className="text-slate-400 group-hover:text-[#00C4CC]"/>
                </button>
              )}
            </div>
            {currentRating && (() => {
              const meta = RATINGS.find(x => x.key === currentRating);
              return meta ? (
                <span data-testid="insp-simple-current-rating"
                  className={`h-10 px-3 inline-flex items-center gap-1 rounded-full text-white font-black uppercase text-[11px] tracking-widest ${meta.solid.split(" ")[0]}`}>
                  <span className="h-2 w-2 rounded-full bg-white"/>{meta.label}
                </span>
              ) : null;
            })()}
          </div>

          {/* Photo strip */}
          <div className="mt-5">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
              Photos ({roomOpts.length + serverPhotos.length})
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {roomOpts.map(o => (
                <div key={o.optId} className="relative aspect-square bg-slate-900 overflow-hidden rounded-xl border-2 border-[#00C4CC]">
                  <img src={o.localUrl} alt="" className="w-full h-full object-cover"/>
                  {o.uploading && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><ArrowsClockwise className="animate-spin text-white" size={20}/></div>}
                  {o.error && (
                    <button type="button" onClick={() => retryUpload(o)}
                      data-testid={`insp-simple-retry-${o.optId}`}
                      className="absolute inset-0 bg-red-600/80 text-white text-xs font-black uppercase flex items-center justify-center gap-1">
                      <ArrowClockwise weight="bold" size={14}/> Retry
                    </button>
                  )}
                </div>
              ))}
              {serverPhotos.map((p, i) => (
                <a key={p.path || i} href={fileUrl(p.path)} target="_blank" rel="noreferrer"
                  data-testid={`insp-simple-photo-${i}`}
                  className="relative aspect-square bg-slate-100 overflow-hidden rounded-xl border-2 border-slate-200 hover:border-[#00C4CC]">
                  {p.media_type === "video"
                    ? <div className="w-full h-full flex items-center justify-center bg-slate-800 text-white text-xs font-black">VIDEO</div>
                    : <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover" loading="lazy"/>}
                </a>
              ))}
              {roomOpts.length + serverPhotos.length === 0 && (
                <div className="col-span-3 sm:col-span-4 p-6 rounded-xl border-2 border-dashed border-slate-300 text-center text-xs text-slate-400 font-black uppercase tracking-widest">
                  No photos yet — take one or add from library below
                </div>
              )}
            </div>
          </div>

          {/* Safety & Filters — quick toggles */}
          <SafetyStrip meta={currentRoom.metadata} onChange={(patch) => {
            patchRoom(currentRoom.room, {
              metadata: { ...(currentRoom.metadata || {}), ...patch },
            });
          }}/>

          {/* Log Appliance */}
          <div className="mt-4">
            <button type="button" onClick={() => setApplianceOpen(true)}
              data-testid="insp-simple-log-appliance"
              className="w-full h-11 rounded-xl border-2 border-slate-300 hover:border-[#00C4CC] active:scale-[0.98] bg-white text-slate-700 font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-1.5 transition-transform">
              + Log Appliance <span className="text-slate-400 font-medium normal-case tracking-normal">(brand · model · serial)</span>
            </button>
          </div>

          {/* Blinds size capture — reveals on Damage/Attn or when toggle is on */}
          {showBlindsSection && (
            <div className="mt-5 rounded-2xl bg-white border-2 border-fuchsia-200 overflow-hidden" data-testid="insp-simple-blinds-block">
              <div className="p-3 flex items-center gap-2 border-b-2 border-fuchsia-100">
                <Blinds weight="fill" size={18} className="text-fuchsia-700"/>
                <div className="font-black uppercase text-xs tracking-widest text-fuchsia-900 flex-1">Blinds / window coverings</div>
                <label className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest cursor-pointer">
                  <input type="checkbox"
                    data-testid="insp-simple-blinds-toggle"
                    checked={blindsDraft.enabled}
                    onChange={e => saveBlinds({ ...blindsDraft, enabled: e.target.checked })}
                    className="h-4 w-4 accent-fuchsia-600"/>
                  Needs replacing
                </label>
              </div>              {blindsDraft.enabled && (
                <div className="p-3 space-y-3">
                  {blindsDraft.entries.map((entry, idx) => (
                    <div key={idx} className="rounded-xl border-2 border-fuchsia-100 bg-fuchsia-50/40 p-3 space-y-2" data-testid={`insp-simple-blinds-entry-${idx}`}>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center h-6 px-2 rounded-full bg-fuchsia-600 text-white text-[10px] font-black uppercase tracking-widest">
                          Size {idx + 1}
                        </span>
                        {blindsDraft.entries.length > 1 && (
                          <button type="button" onClick={() => removeBlindEntry(idx)}
                            data-testid={`insp-simple-blinds-remove-${idx}`}
                            className="ml-auto h-7 px-2 rounded-full border border-slate-300 hover:border-red-500 hover:text-red-600 text-[10px] font-black uppercase tracking-widest text-slate-600">
                            Remove
                          </button>
                        )}
                      </div>
                      <SizePicker label="Width" options={BLIND_WIDTHS} value={entry.width}
                        testidPrefix={`insp-simple-blinds-w-${idx}`}
                        onChange={v => updateBlindEntry(idx, { width: v })}/>
                      <SizePicker label="Height" options={BLIND_HEIGHTS} value={entry.height}
                        testidPrefix={`insp-simple-blinds-h-${idx}`}
                        onChange={v => updateBlindEntry(idx, { height: v })}/>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Qty</div>
                          <input type="number" min={1} value={entry.count}
                            onChange={e => updateBlindEntry(idx, { count: e.target.value })}
                            data-testid={`insp-simple-blinds-count-${idx}`}
                            className="w-full h-11 px-3 rounded-xl border-2 border-slate-300 focus:border-fuchsia-500 bg-white outline-none font-black"/>
                        </div>
                        <div>
                          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Where</div>
                          <input type="text" value={entry.note} maxLength={200}
                            onChange={e => updateBlindEntry(idx, { note: e.target.value })}
                            placeholder="e.g. front window"
                            data-testid={`insp-simple-blinds-note-${idx}`}
                            className="w-full h-11 px-3 rounded-xl border-2 border-slate-300 focus:border-fuchsia-500 bg-white outline-none text-sm"/>
                        </div>
                      </div>
                      {(entry.width || entry.height) && (
                        <div className="p-2 rounded-lg bg-fuchsia-100 text-fuchsia-900 text-xs font-black uppercase tracking-widest inline-flex items-center gap-2">
                          {entry.width || "?"}" W × {entry.height || "?"}" H · qty {entry.count || 1}
                        </div>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addBlindEntry}
                    data-testid="insp-simple-blinds-add"
                    className="w-full h-11 rounded-xl border-2 border-dashed border-fuchsia-400 hover:bg-fuchsia-50 active:scale-[0.98] text-fuchsia-700 font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-1 transition-transform">
                    + Add another size (2nd blind in this room)
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                Notes <span className="font-medium text-slate-400 normal-case tracking-normal">· optional</span>
              </div>
              <VoiceDictateButton
                size="sm"
                testId="insp-simple-notes-mic"
                onTranscript={(text) => {
                  setNotesDraft(prev => {
                    const next = prev ? `${prev.trimEnd()} ${text}` : text;
                    // Persist immediately so we don't lose the dictation on room switch
                    if (currentRoom) {
                      api.patch(`/jobs/${jobId}/inspection/${phase}/room?room=${encodeURIComponent(currentRoom.room)}`,
                        { notes: next }).catch(() => {});
                    }
                    return next;
                  });
                }}
              />
            </div>
            <textarea
              value={notesDraft}
              onChange={e => setNotesDraft(e.target.value)}
              onBlur={() => {
                if (notesDraft !== (currentRoom.notes || "")) {
                  api.patch(`/jobs/${jobId}/inspection/${phase}/room?room=${encodeURIComponent(currentRoom.room)}`,
                    { notes: notesDraft }).catch(() => {});
                }
              }}
              rows={3}
              placeholder="Quick note (e.g. wall scuff behind couch) — or tap the mic to dictate"
              data-testid="insp-simple-notes"
              className="w-full p-3 rounded-xl border-2 border-slate-300 focus:border-[#00C4CC] bg-white text-sm outline-none"
            />
          </div>
        </div>
      </div>

      {/* Hidden file inputs — camera (with capture) + library (no capture) */}
      <input type="file" accept="image/*" capture="environment" ref={cameraRef}
        onChange={e => { onPhoto(e.target.files); e.target.value = ""; }}
        data-testid="insp-simple-photo-input" className="hidden" multiple/>
      <input type="file" accept="image/*" ref={libraryRef}
        onChange={e => { onPhoto(e.target.files); e.target.value = ""; }}
        data-testid="insp-simple-library-input" className="hidden" multiple/>

      {applianceOpen && (
        <ApplianceSheet
          jobId={jobId}
          defaultLocation={roomLabel(currentRoom)}
          onClose={() => setApplianceOpen(false)}
          onSaved={() => { setApplianceOpen(false); toast.success("Appliance logged"); }}
        />
      )}

      {/* Fixed bottom action bar — modernised */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0f172a] text-white border-t-4 border-[#00C4CC] pb-[env(safe-area-inset-bottom,0)]">
        {/* Sticky room banner — prevents the "all photos land under one room" bug
            by keeping the current room name visible right above the camera button
            no matter how far the tech has scrolled. */}
        <div className="bg-[#00C4CC] text-[#0f172a] px-4 py-1.5 flex items-center justify-between gap-2"
          data-testid="insp-simple-current-room-banner">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <span className="text-[9px] font-black uppercase tracking-widest opacity-70 shrink-0">Currently in</span>
            <span className="font-display font-black text-sm truncate">{roomLabel(currentRoom)}</span>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest bg-[#0f172a] text-[#00C4CC] px-2 py-0.5 rounded-full shrink-0">
            {roomIdx + 1}/{total}
          </span>
        </div>
        <div className="max-w-2xl mx-auto p-3 space-y-2">
          <div className="grid grid-cols-4 gap-2">
            {RATINGS.map(r => {
              const active = currentRating === r.key;
              return (
                <button key={r.key} type="button" onClick={() => setRating(r.key)}
                  data-testid={`insp-simple-rating-${r.key}`}
                  className={`h-14 rounded-2xl font-black uppercase text-[11px] tracking-widest text-white transition-transform active:scale-[0.96] ${r.solid} ${
                    active ? `ring-4 ring-offset-2 ring-offset-[#0f172a] ${r.ring}` : "shadow-[0_4px_12px_-4px_rgba(0,0,0,0.4)]"
                  }`}>
                  {r.short}
                </button>
              );
            })}
          </div>
          {/* Photo sources + Prev/Next */}
          <div className="grid grid-cols-12 gap-2">
            <button type="button" onClick={goPrev} disabled={roomIdx === 0}
              data-testid="insp-simple-prev"
              className="col-span-2 h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 active:scale-[0.96] text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center transition-transform">
              <ArrowLeft weight="bold" size={18}/>
            </button>
            <button type="button" onClick={() => cameraRef.current?.click()}
              data-testid="insp-simple-camera"
              className="col-span-4 h-14 rounded-2xl bg-[#00C4CC] hover:bg-[#00d4dd] active:scale-[0.96] text-[#0f172a] font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-1.5 shadow-[0_8px_20px_-8px_rgba(0,196,204,0.7)] transition-transform">
              <Camera weight="fill" size={20}/> Photo
            </button>
            <button type="button" onClick={() => libraryRef.current?.click()}
              data-testid="insp-simple-library"
              className="col-span-3 h-14 rounded-2xl bg-white/10 hover:bg-white/15 active:scale-[0.96] text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-1.5 border border-white/15 transition-transform">
              <ImageIcon weight="fill" size={16}/> Library
            </button>
            <button type="button" onClick={goNext}
              data-testid="insp-simple-next"
              className="col-span-3 h-14 rounded-2xl bg-white hover:bg-slate-100 active:scale-[0.96] text-[#0f172a] font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-1 transition-transform">
              {roomIdx + 1 >= total ? "Review" : "Next"}
              <ArrowRight weight="bold" size={16}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────── Safety & Filter quick-check strip ───────────
const SAFETY_ITEMS = [
  { key: "smoke_detector", label: "Smoke",  emoji: "🚨" },
  { key: "co_detector",    label: "CO",     emoji: "🟢" },
  { key: "filter",         label: "Filter", emoji: "🌀" },
];
const SAFETY_STATES = [
  { key: "ok",       label: "OK",       cls: "bg-emerald-600 text-white" },
  { key: "replaced", label: "Replaced", cls: "bg-cyan-600 text-white" },
  { key: "dead",     label: "Dead",     cls: "bg-red-600 text-white" },
  { key: "missing",  label: "Missing",  cls: "bg-slate-800 text-white" },
];

const FILTER_SIZES = ["16x20x1", "16x25x1", "20x20x1", "20x25x1", "24x24x1", "20x25x4", "16x25x5"];

function SafetyStrip({ meta, onChange }) {
  const filterStatus = meta?.filter?.status || "";
  const showFilterSize = filterStatus === "ok" || filterStatus === "replaced";
  const filterSize = meta?.filter?.size || "";
  return (
    <div className="mt-5 rounded-2xl bg-white border-2 border-slate-200 p-3" data-testid="insp-simple-safety">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Safety &amp; filters</div>
      <div className="space-y-2">
        {SAFETY_ITEMS.map(item => {
          const current = (meta?.[item.key]?.status) || "";
          return (
            <div key={item.key} className="flex items-center gap-2 flex-wrap">
              <div className="min-w-[68px] font-black uppercase text-[11px] tracking-widest text-slate-700">
                {item.emoji} {item.label}
              </div>
              <div className="flex flex-wrap gap-1.5 ml-auto">
                {SAFETY_STATES.map(s => {
                  const on = current === s.key;
                  return (
                    <button key={s.key} type="button"
                      onClick={() => onChange({ [item.key]: {
                        ...(meta?.[item.key] || {}),
                        status: on ? "" : s.key,
                      } })}
                      data-testid={`insp-simple-safety-${item.key}-${s.key}`}
                      className={`h-8 px-2.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-transform active:scale-95 ${
                        on ? s.cls + " shadow-[0_2px_8px_-2px_rgba(0,0,0,0.3)]" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter size picker — reveals only when Filter status is set to OK or Replaced */}
      {showFilterSize && (
        <div className="mt-3 pt-3 border-t-2 border-dashed border-slate-200" data-testid="insp-simple-filter-size">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
            Filter size <span className="font-medium text-slate-400 normal-case tracking-normal">— saves to property so office can reorder</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTER_SIZES.map(s => {
              const on = filterSize === s;
              return (
                <button key={s} type="button"
                  onClick={() => onChange({ filter: { ...(meta?.filter || { status: filterStatus }), size: on ? "" : s } })}
                  data-testid={`insp-simple-filter-size-${s}`}
                  className={`h-9 px-3 rounded-full font-black text-xs tracking-widest transition-transform active:scale-95 ${
                    on ? "bg-cyan-600 text-white shadow-[0_3px_10px_-3px_rgba(8,145,178,0.6)]" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}>
                  {s}"
                </button>
              );
            })}
            <FilterSizeOther current={filterSize} known={FILTER_SIZES}
              onSet={(v) => onChange({ filter: { ...(meta?.filter || { status: filterStatus }), size: v } })}/>
          </div>
          {filterSize && (
            <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-lg bg-cyan-50 text-cyan-900 text-[11px] font-black uppercase tracking-widest">
              {filterSize}" saved for this property
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterSizeOther({ current, known, onSet }) {
  const isCustom = current && !known.includes(current);
  const [open, setOpen] = useState(isCustom);
  const [val, setVal]   = useState(isCustom ? current : "");
  return (
    <>
      <button type="button" onClick={() => setOpen(v => !v)}
        data-testid="insp-simple-filter-size-other"
        className={`h-9 px-3 rounded-full font-black text-[11px] uppercase tracking-widest ${
          open || isCustom ? "bg-[#0f172a] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
        }`}>
        Other{isCustom && `: ${current}`}
      </button>
      {open && (
        <div className="basis-full flex gap-2 mt-2">
          <input type="text" value={val}
            onChange={e => setVal(e.target.value.replace(/[^\dxX.]/g, "").toLowerCase())}
            onBlur={() => { if (val) onSet(val); }}
            placeholder="e.g. 14x30x1"
            data-testid="insp-simple-filter-size-custom"
            className="flex-1 h-10 px-3 rounded-xl border-2 border-slate-300 focus:border-cyan-500 outline-none font-black tracking-wider"/>
          <button type="button" onClick={() => { if (val) onSet(val); setOpen(false); }}
            className="h-10 px-3 rounded-xl bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest">Save</button>
        </div>
      )}
    </>
  );
}

// ─────────── Log Appliance — quick sheet ───────────
function ApplianceSheet({ jobId, defaultLocation, onClose, onSaved }) {
  const [f, setF] = useState({ type: "", brand: "", model: "", serial: "", location: defaultLocation || "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const scanRef = useRef();

  const scan = async (file) => {
    if (!file) return;
    try { setScanPreview(URL.createObjectURL(file)); } catch {}
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/property-assets/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const p = r.data || {};
      setF(prev => ({
        ...prev,
        type:   p.type   || prev.type,
        brand:  p.make   || prev.brand,
        model:  p.model  || prev.model,
        serial: p.serial || prev.serial,
        notes:  p.notes ? (prev.notes ? `${prev.notes} · ${p.notes}` : p.notes) : prev.notes,
      }));
      const gotAny = !!(p.make || p.model || p.serial || p.type);
      toast[gotAny ? "success" : "info"](gotAny ? "Data-plate parsed — review below" : "AI couldn't read the plate — enter manually");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Scan failed");
    } finally { setScanning(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.type.trim() && !f.brand.trim() && !f.model.trim() && !f.serial.trim()) {
      toast.error("Type, Brand, Model, or Serial required"); return;
    }
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/appliance`, { ...f, save_to_property: true });
      onSaved && onSaved();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl"
        data-testid="insp-simple-appliance-sheet">
        <div className="p-4 border-b-2 border-slate-100 flex items-center gap-2">
          <div className="font-display font-black text-xl flex-1">Log Appliance</div>
          <button type="button" onClick={onClose} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X weight="bold" size={18}/>
          </button>
        </div>
        <div className="p-4 space-y-3 max-h-[75vh] overflow-y-auto">
          {/* AI Scan Data-Plate — big primary action */}
          <button type="button" onClick={() => scanRef.current?.click()} disabled={scanning || busy}
            data-testid="insp-simple-appliance-scan"
            className="w-full h-12 rounded-2xl bg-gradient-to-r from-violet-600 to-[#00C4CC] text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98] shadow-[0_8px_24px_-8px_rgba(139,92,246,0.5)] transition-transform">
            <Camera weight="fill" size={16}/> {scanning ? "AI reading nameplate…" : "Scan Data-Plate with AI"}
          </button>
          <input ref={scanRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => { if (e.target.files?.[0]) { scan(e.target.files[0]); e.target.value = ""; }}}/>
          <p className="text-[10px] text-slate-500 text-center font-medium -mt-1">
            Take a photo of the sticker · Claude reads brand · model · serial
          </p>
          {scanPreview && (
            <div className="flex items-center gap-2 p-2 rounded-xl bg-violet-50 border border-violet-200">
              <img src={scanPreview} alt="Nameplate" className="h-14 w-14 object-cover rounded-lg border border-violet-300"/>
              <div className="text-[11px] font-black uppercase tracking-widest text-violet-800">
                Nameplate photo saved to WO
              </div>
            </div>
          )}
          {/* Manual entry (auto-filled by AI when scan lands) */}
          <ApplianceField label="Type"     value={f.type}   onChange={upd("type")}   placeholder="e.g. Fridge, Water Heater" testid="appliance-type"/>
          <div className="grid grid-cols-2 gap-2">
            <ApplianceField label="Brand"  value={f.brand}  onChange={upd("brand")}  placeholder="GE, Whirlpool…" testid="appliance-brand"/>
            <ApplianceField label="Model"  value={f.model}  onChange={upd("model")}  placeholder="Model #" testid="appliance-model"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <ApplianceField label="Serial" value={f.serial} onChange={upd("serial")} placeholder="Serial #" testid="appliance-serial"/>
            <ApplianceField label="Location" value={f.location} onChange={upd("location")} placeholder="Kitchen, Utility…" testid="appliance-location"/>
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Notes</div>
            <textarea rows={2} value={f.notes} onChange={upd("notes")} maxLength={800}
              placeholder="Rust on right side, last serviced 2024"
              data-testid="appliance-notes"
              className="w-full p-2 rounded-xl border-2 border-slate-300 focus:border-[#00C4CC] outline-none text-sm"/>
          </div>
        </div>
        <div className="p-3 border-t-2 border-slate-100 flex gap-2">
          <button type="button" onClick={onClose}
            className="h-11 px-4 rounded-xl border-2 border-slate-300 font-black uppercase text-xs tracking-widest active:scale-[0.98]">Cancel</button>
          <button type="submit" disabled={busy || scanning} data-testid="appliance-submit"
            className="flex-1 h-11 rounded-xl bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest active:scale-[0.98] disabled:opacity-50">
            {busy ? "Saving…" : "Save Appliance"}
          </button>
        </div>
      </form>
    </div>
  );
}
function ApplianceField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <input type="text" value={value} onChange={onChange} placeholder={placeholder} maxLength={120}
        data-testid={testid}
        className="w-full h-11 px-3 rounded-xl border-2 border-slate-300 focus:border-[#00C4CC] outline-none text-sm"/>
    </div>
  );
}

// ─────────── Size chip picker (used for blind width + height) ───────────
function SizePicker({ label, options, value, onChange, testidPrefix }) {
  const isCustom = value && !options.includes(String(value));
  const [customOpen, setCustomOpen] = useState(isCustom);
  const [customVal, setCustomVal]   = useState(isCustom ? String(value) : "");
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
        {label} <span className="font-medium text-slate-400 normal-case tracking-normal">inches</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const active = String(value) === opt;
          return (
            <button key={opt} type="button" onClick={() => { setCustomOpen(false); onChange(opt); }}
              data-testid={`${testidPrefix}-${opt}`}
              className={`h-10 min-w-[3.5rem] px-3 rounded-full font-black text-sm tracking-widest transition-transform active:scale-[0.96] ${
                active
                  ? "bg-fuchsia-600 text-white shadow-[0_4px_12px_-4px_rgba(217,70,239,0.6)]"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}>
              {opt}"
            </button>
          );
        })}
        <button type="button" onClick={() => setCustomOpen(v => !v)}
          data-testid={`${testidPrefix}-other`}
          className={`h-10 px-3 rounded-full font-black text-xs uppercase tracking-widest ${
            customOpen || isCustom ? "bg-[#0f172a] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
          }`}>
          Other{isCustom && `: ${value}"`}
        </button>
      </div>
      {customOpen && (
        <div className="mt-2 flex gap-2">
          <input type="text" inputMode="decimal" value={customVal}
            onChange={e => setCustomVal(e.target.value.replace(/[^\d.]/g, ""))}
            onBlur={() => { if (customVal) onChange(customVal); }}
            placeholder='e.g. 34.5'
            data-testid={`${testidPrefix}-custom`}
            className="flex-1 h-10 px-3 rounded-xl border-2 border-slate-300 focus:border-fuchsia-500 outline-none font-black"/>
          <button type="button" onClick={() => { if (customVal) onChange(customVal); setCustomOpen(false); }}
            className="h-10 px-3 rounded-xl bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest">Save</button>
        </div>
      )}
    </div>
  );
}

// ─────────── Shared top bar ───────────
function TopBar({ title, subtitle, onClose, onSwitchClassic, onBackToStart }) {
  const [moreOpen, setMoreOpen] = useState(false);
  return (
    <div className="bg-[#0f172a] text-white px-4 h-14 flex items-center gap-2 shrink-0">
      <button onClick={onBackToStart || onClose} data-testid="insp-simple-close"
        className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 active:scale-95 transition-transform">
        <X weight="bold" size={20}/>
      </button>
      <div className="min-w-0 flex-1">
        <div className="font-black uppercase text-sm tracking-widest truncate">{title}</div>
        {subtitle && <div className="text-[11px] text-white/60 truncate">{subtitle}</div>}
      </div>
      <div className="relative">
        <button onClick={() => setMoreOpen(v => !v)}
          data-testid="insp-simple-more"
          className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-white/10 active:scale-95 transition-transform">
          <DotsThreeVertical weight="bold" size={22}/>
        </button>
        {moreOpen && (
          <div className="absolute right-0 top-11 w-56 bg-white text-slate-900 shadow-[0_10px_30px_-6px_rgba(0,0,0,0.4)] rounded-xl border border-slate-200 py-1 z-50 overflow-hidden">
            {onSwitchClassic && (
              <button onClick={() => { setMoreOpen(false); onSwitchClassic(); }}
                data-testid="insp-simple-switch-classic"
                className="w-full text-left px-3 py-2 text-xs font-black uppercase tracking-widest hover:bg-slate-100 flex items-center gap-2">
                <ClipboardText size={14}/> Classic Wizard
              </button>
            )}
            <a href="/admin/completed-inspections"
              className="w-full text-left px-3 py-2 text-xs font-black uppercase tracking-widest hover:bg-slate-100 flex items-center gap-2 block">
              <FilePdf size={14}/> View Completed Reports
            </a>
            <a href="/admin/inspection-templates"
              className="w-full text-left px-3 py-2 text-xs font-black uppercase tracking-widest hover:bg-slate-100 flex items-center gap-2 block">
              <FloppyDisk size={14}/> Templates
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
